import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    SettingType,
    CodeBlock,
    BlockType,
    ValidationMessage,
    ValidationMessageType,
    SettingState,
    SettingOption,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import * as tios from './tios';
import * as simulator from './simulator';

export default class ModbusDevice extends BaseFeature {
    constructor() {
        super();
        this.name = 'Modbus Device';
        this.id = 'modbus_device';
        this.singleInstance = false;
        this.settings = this.settings.concat([
            {
                id: 'NAME',
                name: 'Name',
                settingType: SettingType.NAME,
                required: true,
                default: 'MODDevice',
            },
            {
                id: 'PROTOCOL',
                name: 'Connection Type',
                settingType: SettingType.OPTION,
                required: true,
                default: 'modbus_TCP',
                options: [
                    {
                        name: 'TCP',
                        value: 'modbus_TCP',
                    },
                    {
                        name: 'Serial',
                        value: 'modbus_RTU',
                    },
                ],
            },
            {
                id: 'SERIAL_PORT',
                name: 'Serial Port',
                settingType: SettingType.OPTION,
                helpText: 'Add a RS485 tibbit to the project',
                required: true,
                options: [],
                default: '0',
            },
            {
                id: 'DEVICEID',
                name: 'Device ID',
                settingType: SettingType.STRINGVARIABLE,
                max: 254,
                required: true,
                default: '1',
            },
            {
                id: 'DEVICEIP',
                name: 'Device IP',
                settingType: SettingType.STRINGVARIABLE,
                required: true,
                default: '1.0.0.1',
            },
            {
                id: 'DEVICEPORT',
                name: 'Device Port',
                settingType: SettingType.STRINGVARIABLE,
                max: 65535,
                required: true,
                default: '502',
            },
            {
                id: 'REGISTERS',
                name: 'Registers',
                settingType: SettingType.ARRAY,
                required: true,
                refId: 'modbus_device_register',
                default: [],
            },
        ]);
    }

    initSettings(project?: any, parentFeature?: any): void {
        super.initSettings(project, parentFeature, 'NAME');
    }


    getState(settingId: string): SettingState {
        const PROTOCOL = this.getSettingValue('PROTOCOL');
        if (PROTOCOL === 'modbus_RTU') {
            switch (settingId) {
                case 'DEVICEIP':
                case 'DEVICEPORT':
                case 'INTERFACE':
                    return SettingState.HIDDEN;
                default:

                    break;
            }
        } else {
            switch (settingId) {
                case 'DEVICEID':
                case 'SERIAL_PORT':
                    return SettingState.HIDDEN;
                default:

                    break;
            }
        }
        return SettingState.ENABLED;
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        const { project } = this;
        switch (settingId) {
            case 'SERIAL_PORT':
                return project.getModbusSerialPorts();
            default:
                return super.getSettingOptions(settingId);
        }
    }

    validate(): Array<ValidationMessage> {
        const errors: Array<ValidationMessage> = [];
        const { project } = this;

        const protocol = this.getSettingValue('PROTOCOL');
        const serialPort = this.getSettingValue('SERIAL_PORT');
        const registers = this.getSettingValue('REGISTERS');
        const registersHashmap = new Map();
        if (protocol === 'modbus_RTU' && serialPort === '') {
            errors.push({
                message: 'Serial port must be set',
                messageType: ValidationMessageType.ERROR,
                featureId: this.getID(),
                settingId: 'SERIAL_PORT',
            });
        }


        for (let i = 0; i < registers.length; i++) {
            const register = registers[i];
            const address = register.getSettingValue('ADDRESS');
            const registersAtAddress = registersHashmap.get(address);
            if (registersAtAddress) {
                registersAtAddress.push(register);
                registersHashmap.set(address, registersAtAddress);
            } else {
                registersHashmap.set(address, [register]);
            }
        }

        registersHashmap.forEach((value) => {
            if (value.length > 1) {
                for (let i = 0; i < value.length; i++) {
                    errors.push({
                        message: 'Register addresses must be unique',
                        messageType: ValidationMessageType.ERROR,
                        featureId: value[i].getID(),
                        settingId: 'ADDRESS',
                    });
                }
            }
        });
        return errors;
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'simulator':
                return simulator.getCodeBlocks(this);
            default:
                return tios.getCodeBlocks(this);
        }
    }
}
